import type { ReactNode } from 'react'
import { motion } from 'framer-motion'
import { Kicker } from './Kicker'

/**
 * HeroCard v9999 — glass-tile with radial bloom under focal element.
 * Mix of ref1 (dark warm coral dial) + ref2 (light mint pendant halo).
 *
 * Two modes:
 *  - "metric" (default): big numeric hero (time since last feed, etc.)
 *  - "portrait": hero photo anchor for person profile
 */
interface HeroCardProps {
  /** Bloom tint under the hero — 'coral' | 'mint' | 'warm' | undefined */
  bloom?: 'coral' | 'mint' | 'warm'
  /** Small caps kicker line above the hero */
  kicker?: string
  /** Pulsing live-dot before kicker (e.g. "Sleeping now") */
  liveKicker?: boolean
  /** Big numeric metric — the ref1 "23.6°" / ref2 "60%" treatment */
  metric?: string
  /** Suffix rendered small next to the metric (° / % / mL / min) */
  metricUnit?: string
  /** Small headline under metric or as sole heading */
  headline?: string
  /** Subhead — one line context */
  subhead?: string
  /** Meta chips row */
  meta?: ReactNode
  /** Prominent single-tap CTA rendered below meta — computed from care state by the caller */
  smartCTA?: { label: string; onClick: () => void; icon?: ReactNode }
  /** Action button row at bottom of card */
  actions?: ReactNode
  /** Optional background image (hero photography anchor) */
  coverUrl?: string | null
  onClick?: () => void
}

export function HeroCard({
  bloom,
  kicker,
  liveKicker = false,
  metric,
  metricUnit,
  headline,
  subhead,
  meta,
  smartCTA,
  actions,
  coverUrl,
  onClick,
}: HeroCardProps) {
  const bloomClass = bloom === 'coral' ? 'bloom-coral' : bloom === 'mint' ? 'bloom-mint' : bloom === 'warm' ? 'bloom-warm' : ''

  const content = (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: [0.34, 1.56, 0.64, 1] }}
      className={`glass-tile relative overflow-hidden ${bloomClass}`}
      style={{
        padding: '28px 24px 24px',
        minHeight: coverUrl ? '260px' : undefined,
        backgroundImage: coverUrl
          ? `linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 100%), url(${coverUrl})`
          : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* All content sits above the bloom pseudo (z-0) */}
      <div className="relative z-10">
        {kicker && (
          <Kicker live={liveKicker}>
            {kicker}
          </Kicker>
        )}

        {metric && (
          <div className="mt-3 flex items-baseline gap-1">
            <span
              className="numeric-hero text-text-primary"
              style={{
                fontSize: 'clamp(56px, 14vw, 88px)',
                color: coverUrl ? 'white' : undefined,
              }}
            >
              {metric}
            </span>
            {metricUnit && (
              <span
                className="numeric-mono text-text-tertiary"
                style={{
                  fontSize: 'clamp(18px, 4vw, 22px)',
                  color: coverUrl ? 'rgba(255,255,255,0.7)' : undefined,
                }}
              >
                {metricUnit}
              </span>
            )}
          </div>
        )}

        {headline && (
          <h2
            className="mt-2 font-display text-title text-text-primary"
            style={{
              fontWeight: 500,
              letterSpacing: '-0.005em',
              color: coverUrl ? 'white' : undefined,
            }}
          >
            {headline}
          </h2>
        )}

        {subhead && (
          <p
            className="mt-1 text-body text-text-secondary"
            style={{ color: coverUrl ? 'rgba(255,255,255,0.75)' : undefined }}
          >
            {subhead}
          </p>
        )}

        {meta && <div className="mt-4">{meta}</div>}
        {smartCTA && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              smartCTA.onClick()
            }}
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-white/95 text-black px-5 py-3 text-body font-semibold min-h-[44px] active:scale-[0.98] transition-transform"
          >
            {smartCTA.icon && <span aria-hidden="true">{smartCTA.icon}</span>}
            <span>{smartCTA.label}</span>
          </button>
        )}
        {actions && <div className="mt-6 flex flex-wrap gap-2">{actions}</div>}
      </div>
    </motion.div>
  )

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className="block w-full text-left">
        {content}
      </button>
    )
  }
  return content
}

/**
 * LEGACY compat shim — old HeroCard signature used {color, headline, subhead, meta, actions}
 * and rendered a solid dark card. Callers that still use it get the new glass tile
 * with the color mapped to the closest bloom variant.
 */
export function LegacyHeroCard(props: {
  color?: string
  kicker?: string
  liveKicker?: boolean
  headline?: string
  subhead?: string
  meta?: ReactNode
  actions?: ReactNode
  onClick?: () => void
}) {
  const bloom: 'coral' | 'mint' | 'warm' = props.color?.toLowerCase().includes('7') ? 'mint' : 'warm'
  return (
    <HeroCard
      bloom={bloom}
      kicker={props.kicker}
      liveKicker={props.liveKicker}
      headline={props.headline}
      subhead={props.subhead}
      meta={props.meta}
      actions={props.actions}
      onClick={props.onClick}
    />
  )
}
